'use client'

import Script from "next/script";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { captureAttribution } from "@/lib/attribution";

interface SeoScriptsProps {
    gtmId?: string;
}

type GtmWindow = Window & { dataLayer?: unknown[]; __gtmLoaded?: boolean };

// GTM (GA4, Google Ads, Facebook Pixel, Clarity vb. etiketleriyle birlikte)
// sayfa acilisinda degil, ziyaretcinin ilk etkilesiminde (kaydirma, dokunma,
// tiklama, tus) ya da sayfa yuklendikten en gec 4 sn sonra yuklenir. Bu
// etiketler ilk yuklemedeki CPU suresinin buyuk kismini olusturuyordu.
// dataLayer'a bu arada gonderilen olaylar (form gonderimi vb.) kuyrukta
// bekler ve GTM yuklenince islenir.
const GTM_FALLBACK_DELAY_MS = 4000;
const INTERACTION_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"] as const;

function useDeferredGtm(gtmId?: string) {
    useEffect(() => {
        if (!gtmId) return;
        const w = window as GtmWindow;
        if (w.__gtmLoaded) return;

        let timer: number | undefined;

        const load = () => {
            if (w.__gtmLoaded) return;
            w.__gtmLoaded = true;
            cleanup();

            w.dataLayer = w.dataLayer || [];
            w.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
            const script = document.createElement("script");
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
            document.head.appendChild(script);
        };

        const startFallbackTimer = () => {
            timer = window.setTimeout(load, GTM_FALLBACK_DELAY_MS);
        };

        const cleanup = () => {
            INTERACTION_EVENTS.forEach((event) => window.removeEventListener(event, load));
            window.removeEventListener("load", startFallbackTimer);
            window.clearTimeout(timer);
        };

        INTERACTION_EVENTS.forEach((event) => window.addEventListener(event, load, { once: true, passive: true }));
        if (document.readyState === "complete") {
            startFallbackTimer();
        } else {
            window.addEventListener("load", startFallbackTimer, { once: true });
        }

        return cleanup;
    }, [gtmId]);
}

export default function SeoScripts({ gtmId }: SeoScriptsProps) {
    const pathname = usePathname();

    useDeferredGtm(gtmId);

    // UTM / click id'leri her sayfa gorunumunde yakala ki form baska bir
    // sayfada doldurulsa bile kaynak bilgisi kaybolmasin.
    useEffect(() => {
        captureAttribution();
    }, [pathname]);

    return (
        <>
            {/* Zoho CRM Google Ads (GCLID) yakalama: gelen Google Ads
                tiklamasindaki GCLID'i cerezde saklar ve formlardaki gizli
                #zc_gad alanini otomatik doldurur. */}
            <Script
                id="zoho-zcga"
                src="https://crm.zoho.com/crm/javascript/zcga.js"
                // Sayfa yuklendikten sonra; gclid cerezi yine yazilir, form
                // gonderiminde de cerezden okunur (lib/attribution).
                strategy="lazyOnload"
            />
        </>
    );
}

export function GtmNoScript({ gtmId }: { gtmId?: string }) {
    if (!gtmId) return null;
    return (
        <noscript>
            <iframe
                src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
                height="0"
                width="0"
                style={{ display: "none", visibility: "hidden" }}
            />
        </noscript>
    );
}
